import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { KryptonElement } from './krypton-element';
import { waitFor } from '../modules/async';
import { KryptonEventDetail } from './krypton-event-detail';
import { KryptonConstants } from '../modules/krypton_constants';

type AnimationStyle = "appear" | "fade";
type BackgroundStyle = "solid-light" | "solid-dark" | "opaque-light" | "opaque-dark" | "transparent";

export abstract class LayerElement extends KryptonElement {

    _animationStyle: AnimationStyle;
    _backgroundStyle: BackgroundStyle;
    _open: boolean = false;
    _global: boolean = false;

    static styles = css`
        :host {
            display: none;
            /*position: absolute;*/
            top: 0;
            left: 0;
            min-width: 0;
            min-height: 0;
            width: 0;
            height: 0;
            padding: 0;
            margin: 0;
            overflow: hidden;
            box-sizing: border-box;
            -moz-box-sizing: border-box;
            -webkit-box-sizing: border-box;
        }

        :host(.wake) {
            display: block !important;
        }

        :host(.open) {
            min-width: 100%;
            min-height: 100%;
            width: 100%;
            height: 100%;
            overflow: visible;
        }

        :host(.global) {
            position: fixed;
        }

        :host(.bg-solid-light) {
            background-color: white;
        }

        :host(.bg-solid-dark) {
            background-color: #4e4e4e;
        }

        :host(.bg-opaque-light) {
            background-color: rgba(255, 255, 255, 0.8);
        }

        :host(.bg-opaque-dark) {
            background-color: rgba(127, 127, 127, 0.8);
        }

        :host(.a-fade) {
            opacity: 0;
            -webkit-transition: opacity 0.5s 0s ease, height 0s 0.6s, width 0s 0.6s, padding 0s 0.6s, margin 0s 0.6s;
            -moz-transition: opacity 0.5s 0s ease, height 0s 0.6s, width 0s 0.6s, padding 0s 0.6s, margin 0s 0.6s;
            -o-transition: opacity 0.5s 0s ease, height 0s 0.6s, width 0s 0.6s, padding 0s 0.6s, margin 0s 0.6s;
            transition: opacity 0.5s 0s ease, height 0s 0.6s, width 0s 0.6s, padding 0s 0.6s, margin 0s 0.6s;
        }

        :host(.a-fade.open) {
            opacity: 100;
            -webkit-transition: opacity 0.5s 0s ease, height 0s 0s, width 0s 0s, padding 0s 0s, margin 0s 0s;
            -moz-transition: opacity 0.5s 0s ease, height 0s 0s, width 0s 0s, padding 0s 0s, margin 0s 0s;
            -o-transition: opacity 0.5s 0s ease, height 0s 0s, width 0s 0s, padding 0s 0s, margin 0s 0s;
            transition: opacity 0.5s 0s ease, height 0s 0s, width 0s 0s, padding 0s 0s, margin 0s 0s;
        }

        :host(.a-appear) {
            -webkit-transition: height 0s, width 0s;
            -moz-transition: height 0s, width 0s;
            -o-transition: height 0s, width 0s;
            transition: height 0s, width 0s;
        }
    `;

    render() {
        return html`<slot></slot>`;
    }

    constructor() {
        super();
        this.animationStyle = "fade";
        this.backgroundStyle = "solid-light"; //default properties are set in the constructor for IE11 to force a hasChanged event
    }

    get global(): boolean {
        return this._global;
    }

    @property({ type: Boolean, reflect: true })
    set global(global: boolean) {
        const oldValue = this._global;
        this._global = global;
        this.classList[global ? "add" : "remove"]("global");
        this.requestUpdate("global", oldValue);
    }

    get animationStyle(): AnimationStyle {
        return this._animationStyle;
    }

    @property({ type: String })
    set animationStyle(animationStyle: AnimationStyle) {
        const oldValue = this._animationStyle;
        this._animationStyle = animationStyle;
        this.classList.remove("a-" + oldValue);
        this.classList.add("a-" + animationStyle);
        this.requestUpdate("animationStyle", oldValue);
    }

    get backgroundStyle(): BackgroundStyle {
        return this._backgroundStyle;
    }

    @property({ type: String })
    set backgroundStyle(backgroundStyle: BackgroundStyle) {
        const oldValue = this._backgroundStyle;
        this._backgroundStyle = backgroundStyle;
        this.classList.remove("bg-" + oldValue);
        this.classList.add("bg-" + backgroundStyle);
        this.requestUpdate("backgroundStyle", oldValue);
    }

    get open(): boolean {
        return this._open;
    }

    @property({ type: Boolean, reflect: true })
    set open(open: boolean) {
        const oldValue = this._open;
        this._open = open;
        open ? this.animateShow() : this.animateHide();
        this.requestUpdate("open", oldValue);
        if (open) {
            this.scrollIfNeeded();
        }
    }

    scrollIfNeeded() {
    }

    async doOpen(): Promise<void> {
        return new Promise(async resolve => {
            this.open = true;
            await waitFor(KryptonConstants.showHideAnimationTime);
            resolve();
        });
    }

    async doClose(): Promise<void> {
        return new Promise(async resolve => {
            this.open = false;
            await waitFor(KryptonConstants.showHideAnimationTime);
            resolve();
        });
    }

    async animateShow(instant: boolean = false) {
        if (instant) {
            this.classList.add("wake");
            this.classList.add("open");
            this._open = true;
            this.scrollIfNeeded();
        } else {
            await this.async(() => this.classList.add("wake"));
            await this.async(() => this.classList.add("open"), KryptonConstants.layerOpenAnimationTime);
        }
        this.handleEvent(KryptonEventDetail.SHOW);
    }

    async animateHide(instant: boolean = false) {
        if (instant) {
            this.classList.remove("open");
            this.classList.remove("wake");
            this._open = false;
        } else {
            await this.async(() => this.classList.remove("open"));
            await this.async(() => this.classList.remove("wake"), KryptonConstants.showHideAnimationTime);
        }
        this.handleEvent(KryptonEventDetail.HIDE);
    }

    // This is a place holder, elements that extend this class can override if it needs to handle event separately.  page-element is an example
    handleEvent(eventType: any) {
    }

    isVisible() {
        return this.classList.contains("wake") && this.classList.contains("open");
    }

}

